import { format, isValid, parseISO } from 'date-fns';
import { DOCUMENT_TYPE } from '@constants';
import { LenderDocumentTypeEnum } from '@types';

export type ActivityMarkerTone = 'default' | 'highlight';

export type ActivityCoordinate = {
  lat: number;
  lng: number;
};

export type ActivityFileAddress = {
  label?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

export type ActivitySubmission = {
  id: string;
  documentType: string;
  documentName: string;
  submittedAt: string;
  location?: ActivityCoordinate;
  markerTone?: ActivityMarkerTone;
};

export type ActivityHistoryData = {
  submissions: ActivitySubmission[];
};

export type InspectionActivityFile = {
  fileId?: string;
  id?: string;
  documentType?: string;
  createdTime?: string | number;
  uploadedTime?: string | number;
  clientLatitude?: number | null;
  clientLongitude?: number | null;
  clientAddress?: ActivityFileAddress | null;
  networkAddress?: ActivityFileAddress | null;
};

const HIGHLIGHT_DOCUMENT_TYPES = new Set<string>([LenderDocumentTypeEnum.FACE]);

const ACTIVITY_DOCUMENT_NAMES: Record<string, string> = {
  ...Object.fromEntries(
    DOCUMENT_TYPE.map((option) => [option.value, option.label])
  ),
  [LenderDocumentTypeEnum.DRIVER_LICENCE_FRONT]: 'ID Front',
  [LenderDocumentTypeEnum.DRIVER_LICENCE_BACK]: 'ID Back',
  [LenderDocumentTypeEnum.FACE]: "Driver's license and selfie"
};

export type ActivityTimelineItem = ActivitySubmission & {
  markerNumber: number;
  exactTimestamp: string;
  elapsedLabel: string;
};

export type ActivityMarkerGroup = {
  id: string;
  location: ActivityCoordinate;
  markerNumbers: number[];
  documentNames: string[];
  tone: ActivityMarkerTone;
};

export type ActivityLocationCoverage = {
  center: ActivityCoordinate;
  radiusMetres: number;
  maximumSpanMetres: number | null;
  maximumSpanLabel: string | null;
};

const SECOND_IN_MILLISECONDS = 1_000;
const MINUTE_IN_MILLISECONDS = 60_000;
const EARTH_RADIUS_METRES = 6_371_000;
const MINIMUM_COVERAGE_RADIUS_METRES = 50;
const COVERAGE_RADIUS_PADDING = 1.1;

const parseSubmissionDate = (submittedAt: string) => parseISO(submittedAt);

export const formatActivityTimestamp = (submittedAt: string) => {
  const date = parseSubmissionDate(submittedAt);

  return isValid(date) ? format(date, 'dd/MM/yy h:mma') : '';
};

export const formatElapsedDuration = (
  previousTimestamp: string,
  currentTimestamp: string
) => {
  const previousDate = parseSubmissionDate(previousTimestamp);
  const currentDate = parseSubmissionDate(currentTimestamp);

  if (!isValid(previousDate) || !isValid(currentDate)) {
    return '';
  }

  const totalMilliseconds = Math.max(
    0,
    currentDate.getTime() - previousDate.getTime()
  );
  const totalSeconds = Math.floor(totalMilliseconds / SECOND_IN_MILLISECONDS);
  const totalMinutes = Math.floor(totalMilliseconds / MINUTE_IN_MILLISECONDS);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `+${days}d ${hours}h ${minutes}m`;
  }

  if (hours > 0) {
    return `+${hours}h ${minutes}m`;
  }

  if (totalMinutes === 0) {
    return `+${totalSeconds}s`;
  }

  return `+${minutes}m`;
};

export const buildActivityTimeline = (
  submissions: ActivitySubmission[]
): ActivityTimelineItem[] => {
  const orderedSubmissions = submissions
    .map((submission, originalIndex) => ({ submission, originalIndex }))
    .filter(({ submission }) =>
      isValid(parseSubmissionDate(submission.submittedAt))
    )
    .sort((first, second) => {
      const timestampDifference =
        parseSubmissionDate(first.submission.submittedAt).getTime() -
        parseSubmissionDate(second.submission.submittedAt).getTime();

      return timestampDifference || first.originalIndex - second.originalIndex;
    })
    .map(({ submission }) => submission);

  return orderedSubmissions.map((submission, index) => ({
    ...submission,
    markerNumber: index + 1,
    exactTimestamp: formatActivityTimestamp(submission.submittedAt),
    elapsedLabel:
      index === 0
        ? 'Start'
        : formatElapsedDuration(
            orderedSubmissions[index - 1].submittedAt,
            submission.submittedAt
          )
  }));
};

const isValidCoordinate = ({ lat, lng }: ActivityCoordinate) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

const toCoordinateNumber = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

export const parseClientLocation = (
  latitude?: number | null,
  longitude?: number | null
): ActivityCoordinate | undefined => {
  const lat = toCoordinateNumber(latitude);
  const lng = toCoordinateNumber(longitude);

  if (lat == null || lng == null) {
    return undefined;
  }

  const location = { lat, lng };
  return isValidCoordinate(location) ? location : undefined;
};

export const resolveActivityLocation = (
  file: InspectionActivityFile
): ActivityCoordinate | undefined => {
  const clientAddressLocation = parseClientLocation(
    file.clientAddress?.latitude,
    file.clientAddress?.longitude
  );
  if (clientAddressLocation) {
    return clientAddressLocation;
  }

  const clientGpsLocation = parseClientLocation(
    file.clientLatitude,
    file.clientLongitude
  );
  if (clientGpsLocation) {
    return clientGpsLocation;
  }

  return parseClientLocation(
    file.networkAddress?.latitude,
    file.networkAddress?.longitude
  );
};

export const getActivityDocumentName = (documentType: string) =>
  ACTIVITY_DOCUMENT_NAMES[documentType] || documentType;

const toIsoTimestamp = (value?: string | number) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value);
    return isValid(date) ? date.toISOString() : '';
  }

  return typeof value === 'string' ? value : '';
};

export const buildActivityHistoryFromFiles = (
  files?: InspectionActivityFile[] | null
): ActivityHistoryData => ({
  submissions: (files ?? []).flatMap((file) => {
    const id = file.fileId || file.id;
    const documentType = file.documentType;
    const submittedAt = toIsoTimestamp(file.createdTime || file.uploadedTime);

    if (!id || !documentType || !submittedAt) {
      return [];
    }

    return [
      {
        id,
        documentType,
        documentName: getActivityDocumentName(documentType),
        submittedAt,
        location: resolveActivityLocation(file),
        markerTone: HIGHLIGHT_DOCUMENT_TYPES.has(documentType)
          ? 'highlight'
          : 'default'
      }
    ];
  })
});

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

export const calculateActivityDistance = (
  first: ActivityCoordinate,
  second: ActivityCoordinate
) => {
  const latitudeDelta = toRadians(second.lat - first.lat);
  const longitudeDelta = toRadians(second.lng - first.lng);
  const firstLatitude = toRadians(first.lat);
  const secondLatitude = toRadians(second.lat);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(firstLatitude) *
      Math.cos(secondLatitude) *
      Math.sin(longitudeDelta / 2) ** 2;
  const normalizedHaversine = Math.min(1, Math.max(0, haversine));

  return (
    EARTH_RADIUS_METRES *
    2 *
    Math.atan2(
      Math.sqrt(normalizedHaversine),
      Math.sqrt(1 - normalizedHaversine)
    )
  );
};

export const formatActivityDistance = (distanceMetres: number) =>
  `${(distanceMetres / 1_000).toFixed(1)}km`;

export const getActivityLocationCoverage = (
  locations: ActivityCoordinate[]
): ActivityLocationCoverage | null => {
  const validLocations = locations.filter(isValidCoordinate);

  if (validLocations.length === 0) {
    return null;
  }

  const latitudes = validLocations.map(({ lat }) => lat);
  const longitudes = validLocations.map(({ lng }) => lng);
  const center = {
    lat: (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
    lng: (Math.min(...longitudes) + Math.max(...longitudes)) / 2
  };
  const radiusMetres = Math.max(
    MINIMUM_COVERAGE_RADIUS_METRES,
    Math.max(
      ...validLocations.map((location) =>
        calculateActivityDistance(center, location)
      )
    ) * COVERAGE_RADIUS_PADDING
  );

  let maximumSpanMetres: number | null = null;

  for (
    let firstIndex = 0;
    firstIndex < validLocations.length;
    firstIndex += 1
  ) {
    for (
      let secondIndex = firstIndex + 1;
      secondIndex < validLocations.length;
      secondIndex += 1
    ) {
      const distance = calculateActivityDistance(
        validLocations[firstIndex],
        validLocations[secondIndex]
      );

      if (maximumSpanMetres == null || distance > maximumSpanMetres) {
        maximumSpanMetres = distance;
      }
    }
  }

  return {
    center,
    radiusMetres,
    maximumSpanMetres,
    maximumSpanLabel:
      maximumSpanMetres == null
        ? null
        : formatActivityDistance(maximumSpanMetres)
  };
};

const getCoordinateKey = ({ lat, lng }: ActivityCoordinate) =>
  `${lat.toFixed(5)}:${lng.toFixed(5)}`;

export const groupActivityMarkers = (
  timeline: ActivityTimelineItem[]
): ActivityMarkerGroup[] => {
  const markerGroups = new Map<string, ActivityMarkerGroup>();

  timeline.forEach((item) => {
    if (!item.location) {
      return;
    }

    const coordinateKey = getCoordinateKey(item.location);
    const existingGroup = markerGroups.get(coordinateKey);

    if (existingGroup) {
      existingGroup.markerNumbers.push(item.markerNumber);
      existingGroup.documentNames.push(item.documentName);
      if (item.markerTone === 'highlight') {
        existingGroup.tone = 'highlight';
      }
      return;
    }

    markerGroups.set(coordinateKey, {
      id: coordinateKey,
      location: item.location,
      markerNumbers: [item.markerNumber],
      documentNames: [item.documentName],
      tone: item.markerTone ?? 'default'
    });
  });

  return Array.from(markerGroups.values());
};
